import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	effect,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	input,
	OnChanges,
	Output,
	signal,
	SimpleChanges,
	ViewChild,
	viewChildren,
} from '@angular/core';
import { AlternatingMode, ColumnDef, TableModel } from './table.model';
import { flatMap, isNil, last, sum } from 'lodash-es';
import {
	CdkCell,
	CdkCellDef,
	CdkColumnDef,
	CdkFooterCell,
	CdkFooterCellDef,
	CdkFooterRow,
	CdkFooterRowDef,
	CdkHeaderCell,
	CdkHeaderCellDef,
	CdkHeaderRow,
	CdkHeaderRowDef,
	CdkRow,
	CdkRowDef,
	CdkTable,
} from '@angular/cdk/table';
import { firstValueFrom, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CellContentComponent } from '../cell-content/cell-content.component';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { GroeperingDialogComponent, GroeperingOptions } from '../../../../dialogs/groepering/groepering-dialog/groepering-dialog.component';
import { AttrPath } from '@cumlaude/metadata';
import { QpName, QueryParamStateService } from '../../../../services/query-param-state.service';
import { UserService } from '../../../../services/user.service';
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDragPreview, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { isMeatballClick } from '@cumlaude/shared-utils';
import { CategoryFields } from '../../../../services/metadata';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { IconComponent } from '@cumlaude/shared-components-icons';

export declare interface Sort {
	/** The id of the column being sorted. */
	active: string;
	/** The sort direction. */
	direction: SortDirection;
}

export declare type SortDirection = 'asc' | 'desc' | '';

@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./table.component.scss'],
	imports: [
		CdkTable,
		NgClass,
		CdkColumnDef,
		CdkHeaderCellDef,
		CdkHeaderCell,
		NgStyle,
		CellContentComponent,
		CdkCellDef,
		CdkCell,
		CdkFooterCellDef,
		CdkFooterCell,
		CdkHeaderRowDef,
		CdkHeaderRow,
		CdkRowDef,
		CdkRow,
		CdkFooterRowDef,
		CdkFooterRow,
		AsyncPipe,
		CdkDropList,
		CdkDrag,
		CdkDragPreview,
		CdkDragPlaceholder,
		TooltipDirective,
		IconComponent,
	],
})
export class TableComponent<M> implements OnChanges, AfterViewInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);
	protected readonly qp = inject(QueryParamStateService);
	protected readonly userService = inject(UserService);

	@Input()
	model!: TableModel<M>;

	@Input()
	hoverHelp = false;

	@Output()
	rowClick = new EventEmitter<M>();

	@Input()
	exportable = false;

	sortQueryParam = input<QpName>('sortOrder');

	@Input()
	sortDirection!: SortDirection;

	@Input()
	sortActive!: string;

	@Input()
	incremental = false;

	/**
	 * De voorgestelde groeperingen die getoond worden in de groepering dialog.
	 */
	@Input()
	voorgesteldeGroeperingen: AttrPath[] = [];

	/**
	 * Alle beschikbare groeperingen waar de gebruiker uit kan kiezen.
	 */
	allGroups = input<CategoryFields[]>([]);

	/**
	 * De groeperingen welke gebruikt worden om te splitsen in meerdere tabellen.
	 */
	@Input()
	tableGroups: AttrPath[] | undefined;

	/**
	 * De op dit moment geselecteerde groeperingen.
	 */
	@Input()
	huidigeGroeperingen: AttrPath[] = [];

	/**
	 * Extra groeperingen die gebruikt worden om extra informatie op te halen, maar niet als groepering getoond worden.
	 */
	@Input()
	subgroups: AttrPath[] = [];

	/**
	 * Geeft aan of de huidige groeperingen verwijderd mogen worden.
	 */
	@Input()
	currentGroupsDeletable!: boolean;

	hoverC?: number;

	hoverR?: number | 'footer' | 'extraFooter';

	groupingMenuActive = signal<string | undefined>(undefined);

	groeperingen = viewChildren<ElementRef>('groepering');

	dataToRender: Subject<M[]> = new ReplaySubject(1);

	renderBatchSize?: number;

	renderedRows$!: Observable<[number, number]>;

	@ViewChild(CdkTable, { static: true })
	private readonly table_!: CdkTable<M>;

	private dialogRef: DialogRef<unknown, GroeperingDialogComponent> | undefined;

	constructor() {
		effect(async () => {
			if (this.dialogRef) this.dialogRef.close();

			const menuActive = this.groupingMenuActive();
			if (!menuActive) return;

			const groeperingElement = this.groeperingen().find((element) =>
				element.nativeElement.classList.contains(`groepering-${menuActive.replace('.', '-')}`)
			);
			if (!groeperingElement) return;

			const huidigeGroepering = menuActive === 'addGroup' ? undefined : <AttrPath>menuActive.split('.');
			const offsetX = menuActive === 'addGroup' ? -40 : -24;

			let position: ConnectedPosition = {
				originX: 'end',
				originY: 'bottom',
				overlayX: 'start',
				overlayY: 'top',
				offsetX: offsetX,
				offsetY: 1,
			};

			const boundingClientRect = groeperingElement.nativeElement.getBoundingClientRect();
			let height = window.innerHeight - boundingClientRect.bottom - 32;
			if (height < 250) {
				position = {
					originX: 'end',
					originY: 'top',
					overlayX: 'start',
					overlayY: 'bottom',
					offsetX: offsetX,
					offsetY: 1,
				};
				height = boundingClientRect.top - 32;
			}

			let data: GroeperingOptions;
			if (menuActive === 'addGroup' || groeperingElement.nativeElement.classList.contains('change-groups')) {
				data = {
					huidigeGroepering,
					voorgesteldeGroeperingen: this.getVoorgesteldeGroeperingen(),
					alleGroeperingen: this.getAlleGroeperingen(),
					huidigeGroeperingen: this.huidigeGroeperingen,
					deletable: this.currentGroupsDeletable,
					lockable: !groeperingElement.nativeElement.classList.contains('batch'),
				};
			} else {
				data = {
					huidigeGroepering,
					voorgesteldeGroeperingen: [],
					alleGroeperingen: [],
					huidigeGroeperingen: [],
					deletable: false,
					lockable: false,
				};
			}

			this.dialogRef = this.dialog.open(GroeperingDialogComponent, {
				data: data,
				maxHeight: `${height}px`,
				backdropClass: 'cdk-overlay-transparent-backdrop',
				positionStrategy: this.overlay
					.position()
					.flexibleConnectedTo(groeperingElement)
					.withFlexibleDimensions(false)
					.withPositions([position]),
			});

			await firstValueFrom(this.dialogRef.closed);
			this.groupingMenuActive.set(undefined);
		});
	}

	ngAfterViewInit() {
		/**
		 * Bij incrementeel renderen komt er elke keer een aantal rows bij dat minstens dit aantal pixels
		 * inneemt qua hoogte.
		 */
		const pixelsPerBatch = window.innerHeight;

		const tableRowContainer = this.table_._rowOutlet.elementRef.nativeElement.parentElement;
		const tableFooter = this.table_._footerRowOutlet.elementRef.nativeElement.parentElement;

		// Emit als er rows in de DOM bij zijn gekomen.
		this.renderedRows$ = observeMutations(tableRowContainer, { childList: true }).pipe(
			map<MutationRecord[], [number, number]>((entries) => {
				const added: Element[] = flatMap(entries, (entry) => <Element[]>[...entry.addedNodes.values()]).filter(
					(node: Node) => node.nodeType === Node.ELEMENT_NODE
				);
				const nrRows = added.length;
				const nrPixels = sum(added.map((elt) => elt.getBoundingClientRect().height));
				return [nrRows, nrPixels];
			})
		);

		// Zodra er daadwerkelijk één gerenderd is weten we hoe hoog de rows zijn. Render alle volgende in batches van ongeveer een scherm groot.
		this.renderedRows$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([nrRows, nrPixels]) => {
			if (nrRows > 0) this.renderBatchSize = Math.ceil((pixelsPerBatch * nrRows) / nrPixels);
		});

		// Klaar met renderen? Render meer rows als de footer in zicht is (of in zicht komt door scrollen).
		this.renderedRows$
			.pipe(
				switchMap(() => observeIntersection(tableFooter, {}).pipe(map((entries) => last(entries)!.isIntersecting))),
				startWith(true),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((footerInZicht) => {
				if (footerInZicht) this.renderMore();
			});
	}

	renderMore() {
		firstValueFrom(this.dataToRender).then((data) => {
			const len = data.length;
			if (len < this.model.data.length) this.dataToRender.next(this.model.data.slice(0, data.length + (this.renderBatchSize ?? 1)));
		});
	}

	async forceTotalRender(): Promise<void> {
		const rendered = await firstValueFrom(this.dataToRender);
		if (rendered.length == this.model.data.length) return;

		// Zoals ik het nu observeer triggert dataToRender.next() altijd synchroon de DOM update
		// en is het dus eigenlijk niet nodig om daar met een promise op te wachten.
		// Maar als we dat toch willen doen (voor de zekerheid) moeten we eerst de promise
		// aanmaken, anders is de DOM update al geweest en kan je lang wachten.
		const promise = firstValueFrom(this.renderedRows$);
		this.dataToRender.next(this.model.data);
		await promise;
	}

	onClickedBodyCell(rowModel: M, clickHandler: ((rowModel: M) => void) | undefined | null, $event: Event) {
		if (isMeatballClick($event)) return;
		if (isNil(clickHandler)) this.rowClick.emit(rowModel);
		else clickHandler(rowModel);
	}

	onClickedFooterCell(rowModel: TableModel<M>, clickHandler: ((rowModel: TableModel<M>) => void) | undefined | null, $event: Event) {
		if (isMeatballClick($event)) return;
		if (isNil(clickHandler)) return;

		clickHandler(rowModel);
	}

	onHover(r: number | 'footer' | 'extraFooter' | undefined, c: number | undefined) {
		if (this.hoverHelp) {
			this.hoverR = r;
			this.hoverC = c;
		}
	}

	clickSort(column: string, sortable: boolean | undefined) {
		if (!sortable) return;

		const direction = this.sortActive === column ? flipped(this.sortDirection) : 'asc';
		const newSort = { direction, active: column };
		this.qp.dispatch(this.sortQueryParam(), newSort);
	}

	ngOnChanges(changes: SimpleChanges) {
		if ('model' in changes) {
			this.dataToRender.next(this.incremental ? [] : this.model.data);
			this.renderBatchSize = undefined;
		}

		// Forceer een update van de header en footer row
		this.table_.removeHeaderRowDef(null!);
		this.table_.removeFooterRowDef(null!);
	}

	x_columns(columns: string[]): string[] {
		// De extra footer wordt getoond door een extra rij te tonen die geprefixte kolommen bevat
		return columns.map((c) => this.x_column(c));
	}

	x_column(c: string): string {
		return 'x_' + c;
	}

	protected readonly AlternatingMode = AlternatingMode;

	/**
	 * Maximum aantal groeperingen dat kan worden toegevoegd aan de tabel.
	 * Krijgen anders een fout van PostgreSQL als we meer dan 31 groeperingen hebben.
	 */
	private readonly MAX_AANTAL_GROEPERINGEN = 31;

	isGroeperingAvailable() {
		const currentGroupsLength = this.getCurrentGroupsLength();
		const beschikbareAantalGroeperingen = this.getAvailableGroeperingenLength();
		return currentGroupsLength < Math.min(beschikbareAantalGroeperingen, this.MAX_AANTAL_GROEPERINGEN);
	}

	private getAvailableGroeperingenLength() {
		const alleGroeperingenLength = this.getAlleGroeperingen()
			.map((categorie) => categorie.atts.length)
			.reduce((a, b) => a + b, 0);
		const voorgesteldeGroeperingenLength = this.getVoorgesteldeGroeperingen().length;
		return voorgesteldeGroeperingenLength + alleGroeperingenLength;
	}

	private getCurrentGroupsLength() {
		const currentGroupsLength = (this.huidigeGroeperingen ?? []).length;
		const tableGroupsLength = (this.tableGroups ?? []).length;
		const subgroupsLength = this.subgroups.length;
		return tableGroupsLength + currentGroupsLength + subgroupsLength;
	}

	getVoorgesteldeGroeperingen() {
		return this.voorgesteldeGroeperingen.filter((groepering) => this.userService.isAttrPathAllowed(groepering));
	}

	getAlleGroeperingen() {
		return this.allGroups().map((fields) => {
			return {
				...fields,
				atts: fields.atts.filter(
					(field) =>
						!this.voorgesteldeGroeperingen.map((group) => group.join('.')).includes(field.att) &&
						this.userService.isAttrPathAllowed(<AttrPath>field.att.split('.'))
				),
			};
		});
	}

	drop(event: CdkDragDrop<any, any>, columnDefs: ColumnDef<M>[]) {
		const userGroups = [...this.huidigeGroeperingen];

		const lastStickyColumn = columnDefs.filter((columnDef) => columnDef.sticky).length;
		if (event.currentIndex < lastStickyColumn) return;

		moveItemInArray(userGroups, event.previousIndex, event.currentIndex);
		this.qp.dispatch_g(userGroups);
	}
}

function flipped(sort: SortDirection): SortDirection {
	switch (sort) {
		case 'desc':
			return 'asc';
		case 'asc':
			return 'desc';
		case '':
			return '';
	}
}

function observeMutations(node: Node, options: MutationObserverInit): Observable<MutationRecord[]> {
	return new Observable((subscriber) => {
		const observer = new MutationObserver((entries) => subscriber.next(entries));
		observer.observe(node, options);
		return () => observer.disconnect();
	});
}

function observeIntersection(element: Element, options: IntersectionObserverInit): Observable<IntersectionObserverEntry[]> {
	return new Observable((subscriber) => {
		const observer = new IntersectionObserver((entries) => subscriber.next(entries), options);
		observer.observe(element);
		return () => observer.disconnect();
	});
}
